import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface UserProfile {
  queries_used: number;
  queries_limit: number;
  subscription_tier: string;
  tone_preference: string;
  preferred_voice: string | null;
}

interface QueryHistory {
  id: string;
  question: string;
  response: string;
  tone: string;
  created_at: string;
  is_public: boolean;
}

const OraclePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [question, setQuestion] = useState('');
  const [tone, setTone] = useState('balanced');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [showResponse, setShowResponse] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [queryHistory, setQueryHistory] = useState<QueryHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [actualToneUsed, setActualToneUsed] = useState('');
  const [currentQueryId, setCurrentQueryId] = useState<string | null>(null);
  const [isCurrentQueryPublic, setIsCurrentQueryPublic] = useState(false);
  const [makingPublic, setMakingPublic] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedResponse, setDisplayedResponse] = useState('');
  const [isPaidSubscriber, setIsPaidSubscriber] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<string | null>(null);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [currentVoiceUsed, setCurrentVoiceUsed] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Available OpenAI TTS voices
  const voiceOptions = [
    { value: 'alloy', label: 'Alloy', description: 'Neutral and balanced' },
    { value: 'echo', label: 'Echo', description: 'Warm and conversational' },
    { value: 'fable', label: 'Fable', description: 'Expressive and dramatic' },
    { value: 'onyx', label: 'Onyx', description: 'Deep and authoritative' },
    { value: 'nova', label: 'Nova', description: 'Friendly and upbeat' },
    { value: 'shimmer', label: 'Shimmer', description: 'Clear and gentle' },
  ];

  // Load question from URL parameter on mount
  useEffect(() => {
    const questionFromUrl = searchParams.get('q');
    if (questionFromUrl) {
      setQuestion(decodeURIComponent(questionFromUrl));
      // Clear the URL parameter after loading
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    checkUser();
  }, []);

  // Load query history automatically when user is available
  useEffect(() => {
    if (user) {
      loadQueryHistory();
    }
  }, [user]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);

    if (user) {
      await loadProfile(user.id);
    }
  };

  const loadProfile = async (userId: string) => {
    const { data: profileData } = await supabase
      .from('profiles')
      .select('queries_used, queries_limit, subscription_tier, tone_preference, preferred_voice')
      .eq('id', userId)
      .single();

    if (profileData) {
      setProfile(profileData);
      // Set default tone based on subscription tier
      if (profileData.subscription_tier === 'free') {
        setTone('random');
      } else {
        setTone(profileData.tone_preference || 'balanced');
        // Load saved voice preference for paid users
        if (profileData.preferred_voice) {
          setSelectedVoice(profileData.preferred_voice);
        }
      }
    }
  };

  const loadQueryHistory = async () => {
    if (!user) return;
    
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('queries')
        .select('id, question, response, tone_used, created_at, is_public')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      // Map tone_used to tone for display
      const mappedData = (data || []).map(item => ({
        ...item,
        tone: item.tone_used
      }));
      
      setQueryHistory(mappedData);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCopyResponse = async () => {
    try {
      await navigator.clipboard.writeText(response);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleAskAnother = () => {
    setShowResponse(false);
    setResponse('');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleHistoryClick = (item: QueryHistory) => {
    setResponse(item.response);
    setTone(item.tone);
    setCurrentQueryId(item.id);
    setIsCurrentQueryPublic(item.is_public);
    setShowResponse(true);
    setShowHistory(false);
  };

  const toggleHistory = () => {
    if (!showHistory && user) {
      loadQueryHistory();
    }
    setShowHistory(!showHistory);
  };

  const handleMakePublic = async () => {
    if (!currentQueryId || !user) return;

    setMakingPublic(true);
    try {
      const { error } = await supabase
        .from('queries')
        .update({ is_public: true })
        .eq('id', currentQueryId)
        .eq('user_id', user.id);

      if (error) throw error;

      setIsCurrentQueryPublic(true);
      
      // Update history if loaded
      setQueryHistory(prev =>
        prev.map(q =>
          q.id === currentQueryId ? { ...q, is_public: true } : q
        )
      );
    } catch (err: any) {
      console.error('Error making question public:', err);
      setError('Failed to make question public. Please try again.');
    } finally {
      setMakingPublic(false);
    }
  };

  // Typing animation effect for responses
  useEffect(() => {
    if (response && showResponse && isTyping) {
      let index = 0;
      setDisplayedResponse('');
      
      const typingInterval = setInterval(() => {
        if (index < response.length) {
          setDisplayedResponse(response.substring(0, index + 1));
          index++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, 15);

      return () => clearInterval(typingInterval);
    } else if (response && showResponse && !isTyping) {
      setDisplayedResponse(response);
    }
  }, [response, showResponse, isTyping]);

  // Security: Sanitize input to prevent XSS and injection attacks
  const sanitizeInput = (input: string): string => {
    // Remove HTML tags
    let sanitized = input.replace(/<[^>]*>/g, '');
    
    // Remove script tags and their content
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    
    // Remove potentially dangerous characters for SQL injection
    // Keep basic punctuation but remove SQL keywords and special chars
    sanitized = sanitized.replace(/[<>{}[\]\\]/g, '');
    
    // Remove null bytes
    sanitized = sanitized.replace(/\0/g, '');
    
    // Trim whitespace
    sanitized = sanitized.trim();
    
    return sanitized;
  };

  // Validate input length and content
  const validateQuestion = (input: string): { valid: boolean; error?: string } => {
    const sanitized = sanitizeInput(input);
    
    if (!sanitized || sanitized.length === 0) {
      return { valid: false, error: 'Please enter a question' };
    }
    
    if (sanitized.length < 10) {
      return { valid: false, error: 'Question must be at least 10 characters long' };
    }
    
    if (sanitized.length > 500) {
      return { valid: false, error: 'Question must be 500 characters or less' };
    }
    
    // Check for suspicious patterns
    const suspiciousPatterns = [
      /(\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b|\bCREATE\b|\bALTER\b)/gi,
      /(\bUNION\b|\bEXEC\b|\bEXECUTE\b)/gi,
      /(javascript:|data:|vbscript:)/gi,
      /(\bon\w+\s*=)/gi, // Event handlers like onclick=
    ];
    
    for (const pattern of suspiciousPatterns) {
      if (pattern.test(input)) {
        return { valid: false, error: 'Invalid characters detected. Please use only letters, numbers, and basic punctuation.' };
      }
    }
    
    return { valid: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Sanitize and validate input
    const validation = validateQuestion(question);
    if (!validation.valid) {
      setError(validation.error || 'Invalid question');
      return;
    }
    
    const sanitizedQuestion = sanitizeInput(question);

    // Check if user has queries remaining
    if (user && profile) {
      if (profile.queries_used >= profile.queries_limit) {
        setError('You have reached your query limit. Please upgrade your plan to continue.');
        return;
      }
    }

    setLoading(true);
    setError('');
    setResponse('');
    setShowResponse(false);
    setCurrentQueryId(null);
    setIsCurrentQueryPublic(false);
    setIsTyping(false);

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      // If tone is random, pick one of the 5 actual tones
      let actualTone = tone;
      if (tone === 'random') {
        const availableTones = ['mystical', 'practical', 'philosophical', 'balanced', 'humorous'];
        actualTone = availableTones[Math.floor(Math.random() * availableTones.length)];
      }
      
      // Store the actual tone used for display
      setActualToneUsed(actualTone);
      
      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/oracle-query`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            question: sanitizedQuestion, // Send sanitized question
            tone: actualTone,
            userId: currentUser?.id || null,
          }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        
        // Check for specific error types
        if (res.status === 503 || res.status === 502 || res.status === 504) {
          throw new Error('SERVICE_UNAVAILABLE');
        }
        
        throw new Error(errorData.error || 'Failed to get response from oracle');
      }

      const data = await res.json();
      setResponse(data.response);
      setIsTyping(true);
      setShowResponse(true);
      
      // Store the query ID and public status from response
      if (data.queryId) {
        setCurrentQueryId(data.queryId);
        setIsCurrentQueryPublic(data.isPublic || false);
        setIsPaidSubscriber(data.isPaidSubscriber || false);
        
        // Add the new query to the history immediately
        if (currentUser) {
          const newQuery: QueryHistory = {
            id: data.queryId,
            question: sanitizedQuestion, // Use sanitized question
            response: data.response,
            tone: actualTone,
            created_at: new Date().toISOString(),
            is_public: data.isPublic || false,
          };
          setQueryHistory(prev => [newQuery, ...prev]);
        }
      }

      // Refresh profile to get updated query count
      if (currentUser) {
        await loadProfile(currentUser.id);
        // Notify Navbar to refresh profile
        window.dispatchEvent(new Event('profile-updated'));
      }

      setQuestion('');
    } catch (err: any) {
      // Provide user-friendly error messages
      if (err.message === 'SERVICE_UNAVAILABLE') {
        setError(
          'The Oracle is temporarily unavailable due to scheduled maintenance. This is usually brief. Please try again in a few minutes, or contact support if the issue persists.'
        );
      } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError(
          'Unable to connect to the Oracle. Please check your internet connection and try again. If the problem continues, our service may be experiencing temporary issues.'
        );
      } else {
        setError(
          err.message || 'An unexpected error occurred. Please try again. If the issue persists, please contact our support team.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const toneOptions = [
    { value: 'random', label: 'Random Pick', icon: 'ri-shuffle-line', description: 'Surprise me with any tone' },
    { value: 'mystical', label: 'Mystical', icon: 'ri-magic-line', description: 'Spiritual and ethereal wisdom' },
    { value: 'practical', label: 'Practical', icon: 'ri-lightbulb-line', description: 'Straightforward guidance' },
    { value: 'philosophical', label: 'Philosophical', icon: 'ri-book-open-line', description: 'Deep insights' },
    { value: 'balanced', label: 'Balanced', icon: 'ri-scales-line', description: 'Harmonious direction' },
    { value: 'humorous', label: 'Humorous', icon: 'ri-emotion-laugh-line', description: 'Witty wisdom' },
  ];

  const handleSpeak = async () => {
    if (!response) return;

    // If already playing, toggle pause/resume
    if (isSpeaking && audioRef.current) {
      if (!isPaused) {
        audioRef.current.pause();
        setIsPaused(true);
      } else {
        audioRef.current.play();
        setIsPaused(false);
      }
      return;
    }

    setIsLoadingAudio(true);
    setIsSpeaking(false);
    setIsPaused(false);

    try {
      // Determine voice to use
      const isPaid = profile && profile.subscription_tier !== 'free';
      const voiceToUse = isPaid ? selectedVoice : null; // null = random for free users

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/text-to-speech`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            text: response,
            voice: voiceToUse,
          }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate speech');
      }

      const data = await res.json();
      
      // Store which voice was used
      setCurrentVoiceUsed(data.voice);

      // Create audio from base64
      const audioBlob = new Blob(
        [Uint8Array.from(atob(data.audio), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);

      // Clean up previous audio
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsSpeaking(true);
        setIsPaused(false);
        setIsLoadingAudio(false);
      };

      audio.onended = () => {
        setIsSpeaking(false);
        setIsPaused(false);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        setIsPaused(false);
        setIsLoadingAudio(false);
        URL.revokeObjectURL(audioUrl);
      };

      await audio.play();
    } catch (err: any) {
      console.error('TTS error:', err);
      setIsLoadingAudio(false);
      setIsSpeaking(false);
    }
  };

  const handleStopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
    setIsPaused(false);
  };

  const handleVoiceSelect = async (voice: string) => {
    setSelectedVoice(voice);
    setShowVoiceSelector(false);

    // Save to profile if user is logged in and paid
    if (user && profile && profile.subscription_tier !== 'free') {
      await supabase
        .from('profiles')
        .update({ preferred_voice: voice })
        .eq('id', user.id);
    }
  };

  // Cleanup audio on unmount or when modal closes
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, []);

  // Stop audio when response modal closes
  useEffect(() => {
    if (!showResponse) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsSpeaking(false);
      setIsPaused(false);
      setIsLoadingAudio(false);
      setCurrentVoiceUsed(null);
    }
  }, [showResponse]);

  return (
    <div className="min-h-screen relative">
      {/* Mystical Cosmic Background */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/3315dd06c3e67642f54e90e5d56d0279.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          opacity: 0.85
        }}
      />

      {/* Gradient Overlay for better text readability */}
      <div 
        className="fixed inset-0 z-0 bg-gradient-to-b from-purple-900/20 via-transparent to-pink-900/20"
      />

      {/* Content */}
      <div className="relative z-10">
        <Navbar />
        
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <motion.div
                animate={{ 
                  y: [0, -10, 0],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{ 
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="w-24 h-24 flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-600 rounded-full mx-auto mb-6 shadow-2xl"
              >
                <motion.i 
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.8, 1, 0.8]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="ri-sparkling-2-line text-4xl text-white"
                ></motion.i>
              </motion.div>
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="font-serif text-5xl font-bold text-white mb-4 drop-shadow-lg"
              >
                Ask the Oracle
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-xl text-white/90 max-w-2xl mx-auto drop-shadow-md"
              >
                Seek guidance, wisdom, and direction from OhMyGAWD. Ask your question and receive cosmic insights.
              </motion.p>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="flex items-center justify-center gap-4 mt-6 flex-wrap"
              >
                {user && profile && (
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="inline-flex items-center gap-3 px-6 py-3 bg-white/95 backdrop-blur-md rounded-full shadow-lg border border-purple-100"
                  >
                    <div className="flex items-center gap-2">
                      <i className="ri-question-line text-lg text-purple-600"></i>
                      <span className="text-sm font-semibold text-gray-700">
                        {profile.queries_limit - profile.queries_used} queries remaining
                      </span>
                    </div>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <span className="text-sm text-gray-600 capitalize">
                      {profile.subscription_tier} Plan
                    </span>
                  </motion.div>
                )}

                {user && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={toggleHistory}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/95 backdrop-blur-md rounded-full shadow-lg border border-purple-100 hover:border-purple-300 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-history-line text-lg text-purple-600"></i>
                    <span className="text-sm font-semibold text-gray-700">
                      Query History
                    </span>
                  </motion.button>
                )}
              </motion.div>

              {!user && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="mt-6 p-4 bg-white/90 backdrop-blur-md border border-purple-200 rounded-2xl max-w-md mx-auto shadow-lg"
                >
                  <p className="text-sm text-gray-800">
                    <a href="/auth" className="font-semibold hover:underline cursor-pointer text-purple-600">Sign in</a> to track your queries and unlock more features
                  </p>
                </motion.div>
              )}
            </motion.div>

            {/* Oracle Interface */}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Query Form */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-purple-100"
                >
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Tone Selection */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-4">
                        Choose Your Oracle's Tone
                        {profile?.subscription_tier === 'free' && (
                          <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                            Free accounts default to Random
                          </span>
                        )}
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {toneOptions.map((option, index) => (
                          <motion.button
                            key={option.value}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 + index * 0.05, duration: 0.3 }}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            type="button"
                            onClick={() => setTone(option.value)}
                            disabled={profile?.subscription_tier === 'free' && option.value !== 'random'}
                            className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                              tone === option.value
                                ? 'border-purple-600 bg-purple-50 shadow-lg'
                                : 'border-gray-200 hover:border-purple-300'
                            } ${
                              profile?.subscription_tier === 'free' && option.value !== 'random'
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            }`}
                          >
                            <motion.div 
                              animate={tone === option.value ? { rotate: [0, 10, -10, 0] } : {}}
                              transition={{ duration: 0.5 }}
                              className={`w-10 h-10 flex items-center justify-center rounded-full mx-auto mb-2 ${
                                tone === option.value ? 'bg-purple-600' : 'bg-gray-100'
                              }`}
                            >
                              <i className={`${option.icon} text-xl ${
                                tone === option.value ? 'text-white' : 'text-gray-600'
                              }`}></i>
                            </motion.div>
                            <div className={`text-sm font-semibold ${
                              tone === option.value ? 'text-purple-900' : 'text-gray-700'
                            }`}>
                              {option.label}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {option.description}
                            </div>
                            {profile?.subscription_tier === 'free' && option.value !== 'random' && (
                              <div className="mt-2">
                                <i className="ri-lock-line text-xs text-gray-400"></i>
                              </div>
                            )}
                          </motion.button>
                        ))}
                      </div>
                    </div>

                    {/* Question Input */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7, duration: 0.4 }}
                    >
                      <label className="block text-sm font-bold text-gray-900 mb-3">
                        Your Question
                      </label>
                      <textarea
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="What guidance do you seek? Ask about life, love, career, purpose, or any question on your mind..."
                        rows={6}
                        maxLength={500}
                        className="w-full px-5 py-4 text-base border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-gray-500">
                          {question.length}/500 characters
                        </span>
                        {question.length > 450 && (
                          <motion.span 
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="text-xs text-orange-600 font-semibold"
                          >
                            Character limit approaching
                          </motion.span>
                        )}
                      </div>
                    </motion.div>

                    {error && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-red-50 border border-red-200 rounded-2xl"
                      >
                        <p className="text-sm text-red-800">{error}</p>
                      </motion.div>
                    )}

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading || !question.trim()}
                      className="w-full py-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-lg font-bold rounded-2xl hover:shadow-2xl hover:shadow-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap"
                    >
                      {loading ? (
                        <>
                          <motion.i 
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="ri-loader-4-line text-xl"
                          ></motion.i>
                          Consulting the Oracle...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill text-xl"></i>
                          Ask OhMyGAWD
                        </>
                      )}
                    </motion.button>
                  </form>
                </motion.div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Tips Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  whileHover={{ y: -5 }}
                  className="bg-white/95 backdrop-blur-md rounded-3xl shadow-xl p-6 border border-purple-100"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full mb-4">
                    <i className="ri-lightbulb-flash-line text-2xl text-white"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-4">Tips for Better Guidance</h3>
                  <ul className="space-y-3">
                    {[
                      'Be specific and clear with your question',
                      'Choose a tone that resonates with you',
                      'Reflect on the guidance you receive',
                      'Ask follow-up questions for clarity'
                    ].map((tip, index) => (
                      <motion.li 
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <i className="ri-check-line text-lg text-green-600 mt-0.5"></i>
                        <span className="text-sm text-gray-700">{tip}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>

                {/* Upgrade Prompt */}
                {user && profile && profile.subscription_tier === 'free' && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl shadow-xl p-6 text-white"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-md rounded-full mb-4">
                      <i className="ri-vip-crown-line text-2xl text-white"></i>
                    </div>
                    <h3 className="font-bold text-xl mb-2">Unlock More Queries</h3>
                    <p className="text-white/90 text-sm mb-4">
                      Upgrade to Basic or Premium for more monthly queries and enhanced features.
                    </p>
                    <motion.a
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href="/#pricing"
                      className="inline-block px-6 py-3 bg-white text-purple-600 font-bold rounded-xl hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
                    >
                      View Plans
                    </motion.a>
                  </motion.div>
                )}

                {/* Example Questions */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                  className="bg-white/95 backdrop-blur-md rounded-3xl shadow-xl p-6 border border-purple-100"
                >
                  <h3 className="font-bold text-gray-900 mb-4">Example Questions</h3>
                  <div className="space-y-3">
                    {[
                      'What should I focus on in my career right now?',
                      'How can I improve my relationships?',
                      'What is my life purpose?',
                      'How do I overcome my current challenges?',
                    ].map((example, index) => (
                      <motion.button
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8 + index * 0.1 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setQuestion(example)}
                        className="w-full text-left p-3 bg-purple-50 hover:bg-purple-100 rounded-xl transition-all cursor-pointer group"
                      >
                        <p className="text-sm text-gray-700 group-hover:text-purple-900">
                          "{example}"
                        </p>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Response Modal Overlay */}
        <AnimatePresence>
          {showResponse && response && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
              onClick={() => setShowResponse(false)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0, y: 50 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0, y: 50 }}
                transition={{ type: "spring", duration: 0.6, bounce: 0.3 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900 rounded-3xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden border-2 border-purple-400/30"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 backdrop-blur-md px-8 py-6 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <motion.div 
                        animate={{ 
                          rotate: [0, 360],
                          scale: [1, 1.1, 1]
                        }}
                        transition={{ 
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                        className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full shadow-lg"
                      >
                        <i className="ri-sparkling-2-fill text-2xl text-white"></i>
                      </motion.div>
                      <div>
                        <motion.h3 
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 }}
                          className="font-serif text-2xl font-bold text-white"
                        >
                          Oracle's Guidance
                        </motion.h3>
                        <motion.div 
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 }}
                          className="flex items-center gap-2 mt-1"
                        >
                          <span className="inline-block px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs text-cyan-300 font-semibold capitalize">
                            {actualToneUsed || tone} Tone
                          </span>
                          {tone === 'random' && actualToneUsed && (
                            <span className="text-xs text-white/70 italic">
                              (Randomly selected)
                            </span>
                          )}
                        </motion.div>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowResponse(false)}
                      className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
                    >
                      <i className="ri-close-line text-2xl text-white"></i>
                    </motion.button>
                  </div>
                </div>

                {/* Scrollable Content */}
                <div className="px-8 py-6 overflow-y-auto max-h-[calc(85vh-200px)] custom-scrollbar">
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="prose prose-invert max-w-none"
                  >
                    <p className="text-white/95 text-lg leading-relaxed whitespace-pre-wrap">
                      {displayedResponse}
                      {isTyping && (
                        <motion.span
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.5, repeat: Infinity }}
                          className="inline-block w-1 h-5 bg-cyan-400 ml-1"
                        />
                      )}
                    </p>
                  </motion.div>

                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="mt-8 pt-6 border-t border-white/20"
                  >
                    <p className="text-xs text-white/60 italic">
                      This guidance is for entertainment and reflection purposes only. It should not replace professional advice for medical, legal, financial, or mental health matters.
                    </p>
                  </motion.div>
                </div>

                {/* Footer Actions */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 backdrop-blur-md px-8 py-5 border-t border-white/10"
                >
                  <div className="flex gap-3 justify-between items-center flex-wrap">
                    {/* Make Public Button - Only for paid subscribers with private questions */}
                    {user && isPaidSubscriber && !isCurrentQueryPublic && currentQueryId && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleMakePublic}
                        disabled={makingPublic}
                        className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl hover:shadow-xl transition-all whitespace-nowrap cursor-pointer font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {makingPublic ? (
                          <>
                            <motion.i 
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              className="ri-loader-4-line text-lg"
                            ></motion.i>
                            Making Public...
                          </>
                        ) : (
                          <>
                            <i className="ri-global-line text-lg"></i>
                            Make My Question Public
                          </>
                        )}
                      </motion.button>
                    )}
                    
                    {/* Private Badge - Show when question is private (paid subscribers only) */}
                    {isPaidSubscriber && !isCurrentQueryPublic && currentQueryId && (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="px-4 py-2 bg-purple-500/20 border border-purple-400/50 text-purple-300 rounded-xl flex items-center gap-2 font-semibold"
                      >
                        <i className="ri-lock-line text-lg"></i>
                        Private Question
                      </motion.div>
                    )}
                    
                    {/* Public Badge - Show when question is public */}
                    {isCurrentQueryPublic && (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="px-4 py-2 bg-green-500/20 border border-green-400/50 text-green-300 rounded-xl flex items-center gap-2 font-semibold"
                      >
                        <i className="ri-check-line text-lg"></i>
                        Public Question
                      </motion.div>
                    )}

                    <div className="flex gap-3 ml-auto items-center">
                      {/* Voice Selector for Paid Users */}
                      {!isTyping && profile && profile.subscription_tier !== 'free' && (
                        <div className="relative">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowVoiceSelector(!showVoiceSelector)}
                            className="px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer font-semibold flex items-center gap-2"
                          >
                            <i className="ri-user-voice-line text-lg"></i>
                            {selectedVoice ? voiceOptions.find(v => v.value === selectedVoice)?.label : 'Select Voice'}
                            <i className={`ri-arrow-${showVoiceSelector ? 'up' : 'down'}-s-line text-lg`}></i>
                          </motion.button>
                          
                          <AnimatePresence>
                            {showVoiceSelector && (
                              <motion.div
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                className="absolute bottom-full mb-2 right-0 w-64 bg-gray-900/95 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl overflow-hidden z-10"
                              >
                                <div className="p-3 border-b border-white/10">
                                  <p className="text-xs text-white/70 font-semibold">Choose Your Voice</p>
                                </div>
                                <div className="max-h-64 overflow-y-auto">
                                  {voiceOptions.map((voice) => (
                                    <motion.button
                                      key={voice.value}
                                      whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                                      onClick={() => handleVoiceSelect(voice.value)}
                                      className={`w-full px-4 py-3 text-left transition-all cursor-pointer ${
                                        selectedVoice === voice.value ? 'bg-purple-600/30' : ''
                                      }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="text-sm font-semibold text-white">{voice.label}</p>
                                          <p className="text-xs text-white/60">{voice.description}</p>
                                        </div>
                                        {selectedVoice === voice.value && (
                                          <i className="ri-check-line text-lg text-cyan-400"></i>
                                        )}
                                      </div>
                                    </motion.button>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}

                      {/* Speak Button */}
                      {!isTyping && (
                        <div className="flex gap-2 items-center">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleSpeak}
                            disabled={isLoadingAudio}
                            className={`px-6 py-3 backdrop-blur-md border rounded-xl transition-all whitespace-nowrap cursor-pointer font-semibold flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait ${
                              isSpeaking && !isPaused
                                ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-300'
                                : isPaused
                                ? 'bg-yellow-500/20 border-yellow-400/50 text-yellow-300'
                                : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                            }`}
                          >
                            {isLoadingAudio ? (
                              <>
                                <motion.i 
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                  className="ri-loader-4-line text-lg"
                                ></motion.i>
                                Loading...
                              </>
                            ) : (
                              <>
                                <i className={`text-lg ${
                                  isSpeaking && !isPaused 
                                    ? 'ri-pause-line' 
                                    : isPaused 
                                    ? 'ri-play-line'
                                    : 'ri-volume-up-line'
                                }`}></i>
                                {isSpeaking && !isPaused ? 'Pause' : isPaused ? 'Resume' : 'Speak'}
                              </>
                            )}
                          </motion.button>
                          {isSpeaking && (
                            <motion.button
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={handleStopSpeaking}
                              className="px-4 py-3 bg-red-500/20 border border-red-400/50 text-red-300 rounded-xl transition-all whitespace-nowrap cursor-pointer font-semibold flex items-center gap-2 hover:bg-red-500/30"
                            >
                              <i className="ri-stop-line text-lg"></i>
                            </motion.button>
                          )}
                          {/* Show which voice is being used */}
                          {(isSpeaking || isPaused) && currentVoiceUsed && (
                            <motion.span
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="text-xs text-white/60 capitalize"
                            >
                              {currentVoiceUsed} voice
                            </motion.span>
                          )}
                        </div>
                      )}

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleCopyResponse}
                        className={`px-6 py-3 backdrop-blur-md border rounded-xl transition-all whitespace-nowrap cursor-pointer font-semibold flex items-center gap-2 ${
                          copySuccess
                            ? 'bg-green-500/20 border-green-400/50 text-green-300'
                            : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                        }`}
                      >
                        <i className={`text-lg ${copySuccess ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                        {copySuccess ? 'Copied!' : 'Copy'}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleAskAnother}
                        className="px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer font-semibold"
                      >
                        Ask Another Question
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* History Sidebar */}
        <AnimatePresence>
          {showHistory && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                onClick={() => setShowHistory(false)}
              />
              
              {/* Sidebar */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 overflow-hidden flex flex-col"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <motion.div 
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className="w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-md rounded-full"
                    >
                      <i className="ri-history-line text-xl text-white"></i>
                    </motion.div>
                    <h2 className="text-xl font-bold text-white">Query History</h2>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowHistory(false)}
                    className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl text-white"></i>
                  </motion.button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-12">
                      <motion.i 
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="ri-loader-4-line text-3xl text-purple-600"
                      ></motion.i>
                    </div>
                  ) : queryHistory.length === 0 ? (
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center py-12"
                    >
                      <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                        <i className="ri-inbox-line text-3xl text-gray-400"></i>
                      </div>
                      <p className="text-gray-600 font-semibold mb-2">No queries yet</p>
                      <p className="text-sm text-gray-500">Your oracle queries will appear here</p>
                    </motion.div>
                  ) : (
                    queryHistory.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.02, x: -5 }}
                        className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 border border-purple-100 hover:shadow-lg transition-all cursor-pointer"
                        onClick={() => handleHistoryClick(item)}
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full capitalize">
                              {item.tone}
                            </span>
                            {item.is_public && (
                              <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full flex items-center gap-1">
                                <i className="ri-global-line"></i>
                                Public
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(item.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">
                          {item.question}
                        </p>
                        <p className="text-xs text-gray-600 line-clamp-3">
                          {item.response}
                        </p>
                        <div className="mt-3 flex items-center gap-2 text-purple-600">
                          <span className="text-xs font-semibold">View Full Response</span>
                          <motion.i 
                            animate={{ x: [0, 5, 0] }}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="ri-arrow-right-line text-sm"
                          ></motion.i>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <Footer />
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};

export default OraclePage;